import React, { useState, useEffect } from 'react';
import { api, hasSession } from '../utils/api';
import { useI18n } from '../i18n/I18nProvider';

// EmailVerificationBanner nudges signed-in users with an unconfirmed e-mail to
// verify (PIN-224, gating option A). It fetches its own account status and
// renders nothing when verified, signed out, or still loading. The "resend"
// button re-sends the verification link.
// Outline warning icon (triangle-alert) — replaces the ⚠️ emoji per the
// design system (no emoji as UI icons).
const WarnIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    style={{ flex: 'none' }}>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const EmailVerificationBanner = () => {
  const { t } = useI18n();
  const [account, setAccount] = useState(null);
  const [resendState, setResendState] = useState(''); // '' | 'sending' | 'sent' | 'error'

  useEffect(() => {
    if (!hasSession()) return;
    api.getAccount().then(r => r.ok ? r.json() : null).then(setAccount).catch(() => {});
  }, []);

  if (!account || account.EmailVerified) return null;

  const resend = async () => {
    if (!account.Email) return;
    setResendState('sending');
    try {
      const res = await api.resendVerification(account.Email);
      setResendState(res.ok ? 'sent' : 'error');
    } catch {
      setResendState('error');
    }
  };

  return (
    <div className="uh-verify-banner" role="status">
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><WarnIcon /> {t('verifyBanner.text')}</span>
      <span className="uh-verify-banner__action">
        {resendState === 'sent' ? (
          <span className="uh-muted">{t('verifyBanner.sent')}</span>
        ) : (
          <button type="button" className="uh-btn uh-btn-sm" onClick={resend} disabled={resendState === 'sending'}>
            {resendState === 'sending' ? t('verifyBanner.sending') : t('verifyBanner.resend')}
          </button>
        )}
        {resendState === 'error' && <span style={{ color: 'var(--danger-600)', marginLeft: 8 }}>{t('verifyBanner.error')}</span>}
      </span>
    </div>
  );
};

export default EmailVerificationBanner;
